import React from 'react';
import { useLocation } from 'react-router-dom';
import { Map, Star, ArrowRight, Check, Target } from 'lucide-react';
import './Services.css';
import { trackCTAClick, trackWhatsAppClick, waLink } from './analytics';
import AnimatedChatSection from './components/AnimatedChatSection';

const visibilityWaLink = (paket) =>
  waLink(`Halo, saya ingin memilih paket Digital Visibility: ${paket}. Mohon info lebih lanjut.`);

function Services() {
  const location = useLocation();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Offset untuk fixed header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto' // Menggunakan auto untuk scroll lebih cepat
      });
    }
  };

  // SEO: Add structured data for services
  React.useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Technology Solutions & Digital Transformation Services",
      "description": "Comprehensive technology solutions including Software Engineering, AI & Automation, Cloud Solutions, Digital Visibility, and Managed IT Services for businesses in Indonesia",
      "provider": {
        "@type": "Organization",
        "name": "PT Cakra Digital Innovation",
        "url": "https://cakra-digital-deploy.vercel.app"
      },
      "serviceType": "Technology Solutions Services",
      "areaServed": "Indonesia"
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  
  const services = [
    {
      id: 'starter-visibility',
      title: 'Starter Visibility',
      description: 'Setup dasar untuk membantu bisnis muncul di Google dan Google Maps.',
      icon: <Map size={32} />,
      features: [
        'Setup Google Business Profile',
        'Setup listing Google Maps',
        'Optimasi informasi bisnis dasar',
        'Bantuan verifikasi profil'
      ],
      waLink: visibilityWaLink('Starter Visibility'),
      popular: false,
      price: 'Rp 350.000'
    },
    {
      id: 'business-visibility',
      title: 'Business Visibility',
      description: 'Optimasi untuk meningkatkan visibilitas bisnis di pencarian lokal Google.',
      icon: <Map size={32} />,
      features: [
        'Optimasi Google Maps',
        'Setup SEO lokal dasar',
        'Optimasi keyword',
        'Optimasi kategori bisnis',
        'Peningkatan profil'
      ],
      waLink: visibilityWaLink('Business Visibility'),
      popular: true,
      price: 'Rp 750.000'
    },
    {
      id: 'premium-visibility',
      title: 'Premium Visibility',
      description: 'Optimasi lanjutan untuk membantu bisnis bersaing lebih baik di pencarian lokal Google.',
      icon: <Map size={32} />,
      features: [
        'Optimasi Google Maps lanjutan',
        'Optimasi SEO lokal',
        'Targeting keyword',
        'Analisis visibilitas kompetitor',
        'Peningkatan performa profil'
      ],
      waLink: visibilityWaLink('Premium Visibility'),
      popular: false,
      price: 'Rp 1.250.000'
    },
  ];

  return (
    <div className="app">
      <main>
        {/* Hero Section - Clean & Elegant */}
        <section className="hero">
          <div className="hero-container">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-text">Technology Solutions & Digital Transformation</span>
              </div>
              <h1 className="hero-title">
                Solusi Teknologi Komprehensif untuk Transformasi Digital Bisnis Anda
              </h1>
              <p className="hero-subtitle">
                Dari Software Engineering hingga AI & Automation, Cloud Solutions, dan Managed IT Services. Kami membantu UMKM, Startup, dan perusahaan berkembang bertransformasi di era digital dengan solusi yang realistis dan terukur.
              </p>
              <div className="hero-actions">
                <button 
                  onClick={() => {
                    trackCTAClick('Lihat Paket Layanan', location.pathname + location.search + location.hash);
                    scrollToSection('services-section');
                  }}
                  className="btn btn-secondary"
                >
                  <span>Lihat Paket Layanan</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
            
            <div className="hero-visual">
              <div className="hero-graphic">
                <div className="floating-card">
                  <div className="card-icon">
                    <Map size={32} />
                  </div>
                  <div className="card-title">Google Maps</div>
                </div>
                <div className="floating-card">
                  <div className="card-icon">
                    <Target size={32} />
                  </div>
                  <div className="card-title">Local SEO</div>
                </div>
                <div className="floating-card">
                  <div className="card-icon">
                    <Star size={32} />
                  </div>
                  <div className="card-title">Visibility</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Animated Chat Section */}
        <AnimatedChatSection />

        {/* Software Engineering Section */}
        <section id="software-engineering" className="services-category">
          <div className="services-container">
            <header className="services-header">
              <div className="services-badge">Software Engineering</div>
              <h2 className="services-title">Pengembangan Software Komprehensif</h2>
              <p className="services-subtitle">
                Dari Company Profile Website hingga Enterprise Software dan API Integration. Kami membangun solusi software yang skalabel, aman, dan terukur untuk mendukung pertumbuhan bisnis Anda.
              </p>
            </header>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Company Profile Website</h3>
                  <p className="service-description">Website profesional untuk membangun kredibilitas dan kehadiran digital bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Web Application Development</h3>
                  <p className="service-description">Aplikasi web kustom yang dirancang untuk memenuhi kebutuhan spesifik bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Mobile Application Development</h3>
                  <p className="service-description">Pengembangan aplikasi mobile untuk iOS dan Android dengan performa optimal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Check size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Enterprise Software</h3>
                  <p className="service-description">Solusi software enterprise untuk mengoptimalkan operasional bisnis skala menengah.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><ArrowRight size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">API Integration</h3>
                  <p className="service-description">Integrasi API untuk menghubungkan sistem dan aplikasi bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Custom Software Development</h3>
                  <p className="service-description">Pengembangan software kustom sesuai kebutuhan unik bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">System Modernization</h3>
                  <p className="service-description">Modernisasi sistem legacy untuk meningkatkan performa dan keamanan.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI & Automation Section */}
        <section id="ai-automation" className="services-category">
          <div className="services-container">
            <header className="services-header">
              <div className="services-badge">AI & Automation</div>
              <h2 className="services-title">Solusi AI & Automation untuk Efisiensi Bisnis</h2>
              <p className="services-subtitle">
                Dari AI Chatbot hingga Business Automation dan Workflow Automation. Kami membantu Anda mengadopsi AI secara realistis untuk meningkatkan efisiensi dan produktivitas.
              </p>
            </header>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Chatbot</h3>
                  <p className="service-description">Chatbot AI untuk customer service 24/7 dengan respon cepat dan akurat.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Customer Support</h3>
                  <p className="service-description">Sistem dukungan pelanggan berbasis AI untuk meningkatkan kepuasan klien.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Agent Integration</h3>
                  <p className="service-description">Integrasi AI agent ke dalam sistem bisnis Anda untuk otomatisasi cerdas.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Check size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Business Automation</h3>
                  <p className="service-description">Otomatisasi proses bisnis untuk meningkatkan efisiensi dan mengurangi error.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><ArrowRight size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Workflow Automation</h3>
                  <p className="service-description">Otomatisasi workflow untuk mempercepat proses kerja tim.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI API Integration</h3>
                  <p className="service-description">Integrasi API AI (OpenAI, dll) ke dalam aplikasi bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Document Processing</h3>
                  <p className="service-description">Otomatisasi pemrosesan dokumen dengan AI untuk efisiensi administrasi.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Knowledge Base</h3>
                  <p className="service-description">Sistem knowledge base berbasis AI untuk manajemen informasi internal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Check size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI Internal Assistant</h3>
                  <p className="service-description">Asisten AI internal untuk membantu produktivitas tim Anda.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cloud Solutions Section */}
        <section id="cloud-solutions" className="services-category">
          <div className="services-container">
            <header className="services-header">
              <div className="services-badge">Cloud Solutions</div>
              <h2 className="services-title">Solusi Cloud untuk Infrastruktur Modern</h2>
              <p className="services-subtitle">
                Dari Cloud Deployment hingga VPS Management dan CI/CD Setup. Kami membantu Anda mengadopsi cloud secara realistis untuk skalabilitas dan keandalan.
              </p>
            </header>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Cloud Deployment</h3>
                  <p className="service-description">Deployment aplikasi ke cloud (Vercel, Netlify, dll) dengan konfigurasi optimal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">VPS Management</h3>
                  <p className="service-description">Manajemen VPS untuk hosting aplikasi dengan performa dan keamanan optimal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Docker Deployment</h3>
                  <p className="service-description">Deployment dengan Docker untuk konsistensi environment dan skalabilitas.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Check size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">CI/CD Setup</h3>
                  <p className="service-description">Setup pipeline CI/CD untuk automatisasi deployment dan testing.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><ArrowRight size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Business Email</h3>
                  <p className="service-description">Setup email bisnis profesional dengan domain sendiri (Google Workspace, dll).</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Cloud Backup</h3>
                  <p className="service-description">Sistem backup cloud otomatis untuk keamanan data bisnis Anda.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Server Migration</h3>
                  <p className="service-description">Migrasi server ke cloud dengan minimal downtime dan data loss.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Infrastructure Monitoring</h3>
                  <p className="service-description">Monitoring infrastruktur untuk deteksi masalah dan optimasi performa.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Digital Visibility Section */}
        <section id="digital-visibility" className="services-category">
      <div className="services-container">
        <header className="services-header">
          <div className="services-badge">Digital Visibility</div>
          <h2 className="services-title">Optimasi Visibilitas Digital untuk Pertumbuhan Bisnis</h2>
          <p className="services-subtitle">
            Solusi SEO, Google Business Profile, dan Digital Strategy untuk membantu bisnis Anda muncul di pencarian lokal dan menjangkau lebih banyak pelanggan potensial.
          </p>
        </header>

        <div className="services-grid">
          {services.map((service) => (
            <div 
              key={service.id} 
              className={`service-card ${service.popular ? 'popular' : ''}`}
            >
              {service.popular && (
                <div className="service-badge">
                  <Star size={16} />
                  <span>DIANJURKAN</span>
                </div>
              )}
              
              <div className="service-icon-wrapper">
                <div className="service-icon">
                  {service.icon}
                </div>
              </div>

              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                
                {service.price && (
                  <div className="service-price">
                    <span className="price-amount">{service.price}</span>
                  </div>
                )}

                <div className="service-features">
                  <ul className="features-list">
                    {service.features.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <Check size={16} className="feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-action">
                  <a
                    href={service.waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`service-btn ${service.popular ? 'primary' : 'secondary'}`}
                    onClick={() => {
                      trackWhatsAppClick(location.pathname);
                      trackCTAClick(`Pilih Paket - ${service.title}`, location.pathname);
                    }}
                  >
                    <span>Pilih Paket</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="services-footer">
          <div className="footer-content">
            <h3>Butuh Solusi Kustom?</h3>
            <p>Kami dapat menyesuaikan layanan kami untuk memenuhi kebutuhan bisnis spesifik Anda.</p>
          </div>
        </div>

      </div>
        </section>

        {/* Managed IT Services Section */}
        <section id="managed-it" className="services-category">
          <div className="services-container">
            <header className="services-header">
              <div className="services-badge">Managed IT Services</div>
              <h2 className="services-title">Layanan IT Managed untuk Keandalan Operasional</h2>
              <p className="services-subtitle">
                Dari Remote IT Support hingga Infrastructure Monitoring dan Backup Management. Kami membantu menjaga keandalan sistem IT bisnis Anda dengan solusi yang realistis dan terukur.
              </p>
            </header>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Remote IT Support</h3>
                  <p className="service-description">Dukungan IT remote untuk troubleshooting dan maintenance sistem.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Preventive Maintenance</h3>
                  <p className="service-description">Maintenance preventif untuk mencegah masalah sebelum terjadi.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Infrastructure Monitoring</h3>
                  <p className="service-description">Monitoring infrastruktur 24/7 untuk deteksi masalah dini.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Check size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Website Monitoring</h3>
                  <p className="service-description">Monitoring website untuk uptime dan performa optimal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><ArrowRight size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Backup Management</h3>
                  <p className="service-description">Manajemen backup otomatis untuk keamanan data bisnis.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Map size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Security Update</h3>
                  <p className="service-description">Update keamanan rutin untuk melindungi sistem dari ancaman.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Star size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">System Maintenance</h3>
                  <p className="service-description">Maintenance sistem rutin untuk performa dan keandalan optimal.</p>
                </div>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon"><Target size={32} /></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Technical Support</h3>
                  <p className="service-description">Dukungan teknis untuk masalah IT bisnis Anda.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

export default Services;
